import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Clock } from "lucide-react";
import api from "@/lib/api";

const MAX_ATTEMPTS = 30; // ~60 seconds total
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    let attempts = 0;
    let consecutiveErrors = 0;

    const poll = async () => {
      attempts++;
      setAttempt(attempts);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        consecutiveErrors = 0;
        setPaymentData(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("timeout");
        }
      } catch {
        consecutiveErrors++;
        // Retry on transient errors instead of giving up immediately
        if (consecutiveErrors >= 5 || attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md border border-zinc-200 rounded-2xl shadow-sm animate-fade-in" data-testid="payment-status-card">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <div data-testid="payment-loading">
              <Loader2 className="h-16 w-16 text-emerald-600 mx-auto mb-4 animate-spin" />
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-2">Vérification du paiement...</h2>
              <p className="text-sm text-zinc-500">Cela peut prendre jusqu'à 1 minute. Veuillez patienter.</p>
              {attempt > 3 && (
                <p className="text-xs text-zinc-400 mt-3">Tentative {attempt}/{MAX_ATTEMPTS}</p>
              )}
            </div>
          )}
          {status === "success" && (
            <div data-testid="payment-success">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-2">Réservation confirmée!</h2>
              <p className="text-sm text-zinc-500 mb-2">Votre séjour camping a été réservé avec succès.</p>
              {paymentData && (
                <p className="text-lg font-bold text-zinc-950 mb-6">
                  {(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              )}
              <Link to="/my-bookings" data-testid="goto-bookings-link">
                <Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl">Voir mes réservations</Button>
              </Link>
            </div>
          )}
          {status === "expired" && (
            <div data-testid="payment-failed">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-2">Session expirée</h2>
              <p className="text-sm text-zinc-500 mb-6">La session de paiement a expiré.</p>
              <div className="flex gap-3 justify-center">
                <Link to="/spaces"><Button variant="outline" className="rounded-xl">Explorer les terrains</Button></Link>
                <Link to="/my-bookings"><Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl">Mes réservations</Button></Link>
              </div>
            </div>
          )}
          {status === "timeout" && (
            <div data-testid="payment-pending">
              <Clock className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-2">Vérification en cours</h2>
              <p className="text-sm text-zinc-500 mb-2">Si votre paiement a été effectué auprès de Stripe, votre réservation sera confirmée automatiquement dès réception du paiement.</p>
              <p className="text-sm text-zinc-500 mb-6">Consultez « Mes réservations » dans quelques instants pour vérifier le statut.</p>
              <div className="flex gap-3 justify-center">
                <Link to="/spaces"><Button variant="outline" className="rounded-xl">Explorer les terrains</Button></Link>
                <Link to="/my-bookings"><Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl">Mes réservations</Button></Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
